import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green, red } from "@material-ui/core/colors";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  title: {
    paddingBottom: 0,
  },
  subtitle: {
    padding: "0 24px 8px",
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  requirements: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  reqItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
    fontSize: "0.8rem",
  },
  ok: {
    color: green[600],
    fontSize: "1rem",
  },
  fail: {
    color: red[400],
    fontSize: "1rem",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const checks = [
  { label: "Mínimo 8 caracteres", test: p => p.length >= 8 },
  { label: "Contém letra", test: p => /[A-Za-z]/.test(p) },
  { label: "Contém número", test: p => /\d/.test(p) },
  { label: "Contém caractere especial", test: p => /[^A-Za-z\d]/.test(p) },
];

const ChangePasswordModal = ({ open, userId, onSuccess }) => {
  const classes = useStyles();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const allChecksPass = checks.every(c => c.test(password));
  const passwordsMatch = password === confirm && confirm.length > 0;
  const canSubmit = allChecksPass && passwordsMatch && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const { data } = await api.put("/users/change-password/me", {
        password,
        confirmPassword: confirm,
      });
      toast.success("Senha alterada com sucesso!");
      setPassword("");
      setConfirm("");
      if (onSuccess) onSuccess(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableBackdropClick disableEscapeKeyDown>
      <DialogTitle className={classes.title}>Troca de senha obrigatória</DialogTitle>
      <Typography className={classes.subtitle}>
        O administrador solicitou que você crie uma nova senha antes de continuar.
      </Typography>
      <DialogContent>
        <TextField
          className={classes.field}
          label="Nova senha"
          type={showPass ? "text" : "password"}
          value={password}
          onChange={e => setPassword(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPass(v => !v)}>
                  {showPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div className={classes.requirements}>
          {checks.map(c => {
            const ok = c.test(password);
            return (
              <div key={c.label} className={classes.reqItem}>
                {ok
                  ? <CheckCircle className={classes.ok} />
                  : <Cancel className={classes.fail} />}
                <span style={{ color: ok ? green[700] : undefined }}>{c.label}</span>
              </div>
            );
          })}
        </div>

        <TextField
          className={classes.field}
          label="Confirmar nova senha"
          type={showConfirm ? "text" : "password"}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          error={confirm.length > 0 && !passwordsMatch}
          helperText={confirm.length > 0 && !passwordsMatch ? "As senhas não conferem" : ""}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowConfirm(v => !v)}>
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions>
        <div className={classes.btnWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            Salvar nova senha
          </Button>
          {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
